import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, ArrowRight, TrendingUp, Building2, Users, DollarSign,
  Plus, Edit, Trash2, Calendar, Phone, ChevronRight, Search, RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { id: "discovered", label: "Discovered", color: "bg-gray-500" },
  { id: "contacted", label: "Contacted", color: "bg-blue-500" },
  { id: "meeting", label: "Meeting", color: "bg-purple-500" },
  { id: "due_diligence", label: "Due Diligence", color: "bg-orange-500" },
  { id: "negotiation", label: "Negotiation", color: "bg-yellow-500" },
  { id: "closed", label: "Closed", color: "bg-green-500" },
  { id: "passed", label: "Passed", color: "bg-red-500" }
];

const InvestorPipelinePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState({});
  const [stats, setStats] = useState({});
  const [startups, setStartups] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedStartup, setSelectedStartup] = useState(null);
  
  // Form state
  const [dealForm, setDealForm] = useState({
    stage: "discovered",
    notes: "",
    interest_level: "medium",
    deal_size: "",
    next_action: "",
    next_action_date: ""
  });

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchPipeline();
    fetchStartups();
  }, [isAuthenticated, authLoading]);

  const fetchPipeline = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/investors/pipeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPipeline(response.data.pipeline || {});
      setStats(response.data.stats || {});
      setLoading(false);
    } catch (error) {
      console.error("Error fetching pipeline:", error);
      toast({ title: "Error", description: "Failed to load pipeline", variant: "destructive" });
      setLoading(false);
    }
  };

  const fetchStartups = async () => {
    try {
      const response = await axios.get(`${API}/startups`);
      setStartups(response.data);
    } catch (error) {
      console.error("Error fetching startups:", error);
    }
  };

  const handleAddToPipeline = async () => {
    if (!selectedStartup) {
      toast({ title: "Error", description: "Please select a startup", variant: "destructive" });
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/investors/pipeline`, {
        startup_id: selectedStartup.id,
        startup_name: selectedStartup.name,
        ...dealForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: "Added to pipeline" });
      setAddDialogOpen(false);
      setSelectedStartup(null);
      setDealForm({ stage: "discovered", notes: "", interest_level: "medium", deal_size: "", next_action: "", next_action_date: "" });
      fetchPipeline();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to add", variant: "destructive" });
    }
  };

  const handleUpdateDeal = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/investors/pipeline/${selectedDeal.id}`, dealForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: "Pipeline updated" });
      setEditDialogOpen(false);
      setSelectedDeal(null);
      fetchPipeline();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  const handleRemoveDeal = async (dealId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/investors/pipeline/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Removed", description: "Deal removed from pipeline" });
      fetchPipeline();
    } catch (error) {
      toast({ title: "Error", description: "Failed to remove", variant: "destructive" });
    }
  };

  const openEditDialog = (deal) => {
    setSelectedDeal(deal);
    setDealForm({
      stage: deal.stage || "discovered",
      notes: deal.notes || "",
      interest_level: deal.interest_level || "medium",
      deal_size: deal.deal_size || "",
      next_action: deal.next_action || "",
      next_action_date: deal.next_action_date || ""
    });
    setEditDialogOpen(true);
  };

  const filteredStartups = startups.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.industry?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <Header />
      
      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Deal Flow Pipeline</h1>
              <p className="opacity-90">Track and manage your investment opportunities</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={fetchPipeline} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <RefreshCw className="h-4 w-4 mr-2" />Refresh
              </Button>
              <Button onClick={() => setAddDialogOpen(true)} className="bg-white text-blue-700 hover:bg-gray-100">
                <Plus className="h-4 w-4 mr-2" />Add Deal
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-white">{stats.total_deals || 0}</p>
              <p className="text-sm text-gray-400">Total Deals</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">{stats.active_deals || 0}</p>
              <p className="text-sm text-gray-400">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{stats.closed_deals || 0}</p>
              <p className="text-sm text-gray-400">Closed</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-400">{stats.passed_deals || 0}</p>
              <p className="text-sm text-gray-400">Passed</p>
            </CardContent>
          </Card>
        </div>

        {/* Pipeline Board */}
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {STAGES.map((stage) => (
              <div key={stage.id} className="w-72 flex-shrink-0">
                <div className={`${stage.color} rounded-t-lg px-4 py-2 flex items-center justify-between`}>
                  <span className="text-white font-semibold">{stage.label}</span>
                  <Badge className="bg-white/20 text-white">
                    {(pipeline[stage.id] || []).length}
                  </Badge>
                </div>
                <div className="bg-gray-800/50 rounded-b-lg p-3 min-h-[400px] border border-gray-700 border-t-0 space-y-3">
                  {(pipeline[stage.id] || []).map((deal) => (
                    <Card key={deal.id} className="bg-gray-900/80 border-gray-700 hover:border-gray-500 transition-all cursor-pointer">
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="text-white font-medium text-sm truncate flex-1">{deal.startup_name}</h4>
                          <Badge className={
                            deal.interest_level === 'high' ? 'bg-green-600' :
                            deal.interest_level === 'low' ? 'bg-gray-600' : 'bg-blue-600'
                          } variant="secondary">
                            {deal.interest_level}
                          </Badge>
                        </div>
                        {deal.deal_size && (
                          <p className="text-green-400 text-xs mb-2"><DollarSign className="h-3 w-3 inline" />{deal.deal_size}</p>
                        )}
                        {deal.next_action && (
                          <p className="text-gray-400 text-xs mb-2 truncate">→ {deal.next_action}</p>
                        )}
                        {deal.notes && (
                          <p className="text-gray-500 text-xs line-clamp-2 mb-2">{deal.notes}</p>
                        )}
                        <div className="flex justify-end gap-1 mt-2">
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-400 hover:text-white" onClick={() => openEditDialog(deal)}>
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-400 hover:text-red-300" onClick={() => handleRemoveDeal(deal.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {(pipeline[stage.id] || []).length === 0 && (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No deals in this stage
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add Deal Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add to Pipeline</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Search Startup</label>
              <Input 
                placeholder="Search by name or industry..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-gray-900 border-gray-600"
              />
            </div>
            <div className="max-h-40 overflow-y-auto border border-gray-700 rounded-lg">
              {filteredStartups.slice(0, 10).map((startup) => (
                <div 
                  key={startup.id}
                  onClick={() => setSelectedStartup(startup)}
                  className={`p-3 border-b border-gray-700 last:border-0 cursor-pointer hover:bg-gray-700/50 ${
                    selectedStartup?.id === startup.id ? 'bg-blue-900/30 border-l-2 border-l-blue-500' : ''
                  }`}
                >
                  <p className="text-white font-medium">{startup.name}</p>
                  <p className="text-gray-400 text-sm">{startup.industry} • {startup.ecosystem}</p>
                </div>
              ))}
            </div>
            {selectedStartup && (
              <>
                <div className="bg-gray-900/50 p-3 rounded-lg">
                  <p className="text-green-400 font-medium">Selected: {selectedStartup.name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Stage</label>
                    <Select value={dealForm.stage} onValueChange={(v) => setDealForm({...dealForm, stage: v})}>
                      <SelectTrigger className="bg-gray-900 border-gray-600"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Interest Level</label>
                    <Select value={dealForm.interest_level} onValueChange={(v) => setDealForm({...dealForm, interest_level: v})}>
                      <SelectTrigger className="bg-gray-900 border-gray-600"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Deal Size</label>
                  <Input 
                    placeholder="e.g., $50K - $100K"
                    value={dealForm.deal_size}
                    onChange={(e) => setDealForm({...dealForm, deal_size: e.target.value})}
                    className="bg-gray-900 border-gray-600"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Notes</label>
                  <Textarea 
                    placeholder="Add notes..."
                    value={dealForm.notes}
                    onChange={(e) => setDealForm({...dealForm, notes: e.target.value})}
                    className="bg-gray-900 border-gray-600"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-gray-600">Cancel</Button>
            <Button onClick={handleAddToPipeline} className="bg-blue-600 hover:bg-blue-700" disabled={!selectedStartup}>Add to Pipeline</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Deal Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Deal: {selectedDeal?.startup_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Stage</label>
                <Select value={dealForm.stage} onValueChange={(v) => setDealForm({...dealForm, stage: v})}>
                  <SelectTrigger className="bg-gray-900 border-gray-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Interest Level</label>
                <Select value={dealForm.interest_level} onValueChange={(v) => setDealForm({...dealForm, interest_level: v})}>
                  <SelectTrigger className="bg-gray-900 border-gray-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Deal Size</label>
              <Input 
                placeholder="e.g., $50K - $100K"
                value={dealForm.deal_size}
                onChange={(e) => setDealForm({...dealForm, deal_size: e.target.value})}
                className="bg-gray-900 border-gray-600"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Next Action</label>
              <Input 
                placeholder="e.g., Schedule follow-up call"
                value={dealForm.next_action}
                onChange={(e) => setDealForm({...dealForm, next_action: e.target.value})}
                className="bg-gray-900 border-gray-600"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Notes</label>
              <Textarea 
                placeholder="Add notes..."
                value={dealForm.notes}
                onChange={(e) => setDealForm({...dealForm, notes: e.target.value})}
                className="bg-gray-900 border-gray-600"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-gray-600">Cancel</Button>
            <Button onClick={handleUpdateDeal} className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-6">
        <div className="container mx-auto px-6 text-center text-gray-400">
          <p>© 2026 SKI AXIS. Investment pipeline powered by governance intelligence.</p>
        </div>
      </footer>
    </div>
  );
};

export default InvestorPipelinePage;
